import { Corporation, Vehicle, WithContext, WebSite } from 'schema-dts';
import { helmetJsonLdProp } from 'react-schemaorg';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';

import removeEmptyFields from '../../../../utils/removeEmptyFields';
import {
  THeaderComponent,
  TJSONLDSeoTagsProps,
  TRouteData,
  TJSONLDSitecoreContext,
  TproductSeoTagComponent,
} from '../types/jsonldSeoTags.types';
import { TJsonLDObj } from '../../../../types/sitecore.types';

export class JSONLDSeoModel {
  private readonly route: TRouteData;
  private readonly context?: TJSONLDSitecoreContext;

  constructor(props: TJSONLDSeoTagsProps) {
    this.route = props.route;
    this.context = props.sitecoreContext as TJSONLDSitecoreContext;
  }

  get productSeoTags(): TproductSeoTagComponent | undefined {
    const mainComponentsList = (this.route?.placeholders?.['jss-main'] as ComponentRendering[]) || [];
    return mainComponentsList?.find((comp) => comp?.componentName === 'ProductSeoTags');
  }

  get title(): string {
    return (
      this.productSeoTags?.fields?.apiData?.seo?.title || this.route?.fields?.metaTitle?.value || this.context?.metaData?.brandName || ''
    );
  }

  get productSchemaBodyType(): string {
    return this.productSeoTags?.fields?.apiData?.seo?.seoBodyType || this.route?.fields?.bodyType?.value || '';
  }

  get productSchemaCargoVolume(): string {
    return this.productSeoTags?.fields?.apiData?.seo?.seoCargoVolume || this.route?.fields?.cargoVolume?.value || '';
  }

  get productSchemaFuelType(): string {
    return this.productSeoTags?.fields?.apiData?.seo?.seoFuelType || this.route?.fields?.fuelType?.value || '';
  }

  get productImage(): string {
    return this.productSeoTags?.fields?.apiData?.assets?.find((asset) => asset?.role === 'main')?.full || '';
  }

  get productType(): string {
    return 'Vehicle';
  }

  get schemaType(): string {
    return 'Corporation';
  }

  get schemaName(): string {
    return this.context?.metaData?.brandName || '';
  }

  get schemaDescription(): string {
    return this.route?.fields?.metaDescription?.value || '';
  }

  get schemaUrl(): string {
    return this.route?.fields?.metaUrl?.value || '';
  }

  get schemaLogo(): string {
    return this.context?.metaData?.officialLogo?.src || '';
  }

  get schemaSameAs(): Array<string> {
    return this.context?.metaData?.socialMediaLinks?.map((link) => link?.url as string) || [];
  }

  get productUrl(): string {
    return this.context?.ContextExtensions?.hrefLanguageList?.find((item) => item?.langCode === this.context?.language)?.langUrl || '';
  }

  get corporationJSONLD(): TJsonLDObj {
    const jsonLD = removeEmptyFields({
      '@context': 'https://schema.org',
      '@type': this.schemaType,
      name: this.schemaName,
      description: this.schemaDescription,
      url: this.schemaUrl,
      logo: this.schemaLogo,
      sameAs: this.schemaSameAs,
    }) as WithContext<Corporation>;
    return helmetJsonLdProp<Corporation>(jsonLD);
  }

  /* eslint-disable-next-line */
  get vehicleJSONLD(): TJsonLDObj | undefined {
    if (this.productSchemaBodyType || this.productSchemaCargoVolume || this.productSchemaFuelType) {
      const corporationJSONLD = removeEmptyFields({
        '@context': 'https://schema.org',
        '@type': this.schemaType,
        name: this.schemaName,
        description: this.schemaDescription,
        url: this.schemaUrl,
        logo: this.schemaLogo,
        sameAs: this.schemaSameAs,
      });
      const jsonLD = removeEmptyFields({
        '@context': 'https://schema.org',
        '@type': this.productType,
        name: this.title,
        manufacturer: corporationJSONLD,
        url: this.productUrl,
        bodytype: this.productSchemaBodyType,
        CargoVolume: this.productSchemaCargoVolume,
        fuelType: this.productSchemaFuelType,
        image: this.productImage,
      }) as WithContext<Vehicle>;

      return helmetJsonLdProp<Vehicle>(jsonLD);
    }
    return undefined;
  }

  get pageJSONLD(): TJsonLDObj {
    return this.vehicleJSONLD || this.corporationJSONLD;
  }

  get hostUrl(): string {
    return this.context?.hostVirtualFolder || '';
  }

  get homePage(): boolean {
    const homeTemplateId = 'cac1b333-dec6-48cc-bdbc-169d4dda9da0';
    return this.route?.templateId === homeTemplateId;
  }

  get headerComponent(): THeaderComponent | undefined {
    return this.route?.placeholders?.['jss-header']?.[0] as ComponentRendering;
  }

  get searchUrl(): string {
    const param = this.headerComponent?.fields?.search?.link?.value?.href?.split('/') || [];
    const lastParam = param[param.length - 1];
    return lastParam ? `${this.hostUrl}/${lastParam}` : '';
  }

  get searchboxJSONLD(): TJsonLDObj {
    const jsonLD = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: this.hostUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: this.searchUrl + '?q={search_phrase}',
        },
        'query-input': 'required name=search_phrase',
      },
    } as WithContext<WebSite>;

    return helmetJsonLdProp<WebSite>(jsonLD);
  }
}
